import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { SortType } from "../../../../../models/SortType";
import { WalletApiRow } from "../../../../../models/Swagstore/SwagstoreWalletListing";
import { formatWalletBalance, formatWalletDate } from "./walletFormatters";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import {
  getModernPageColors,
  modernEmptyStateSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTablePaperSx,
  modernTableRowSx,
} from "../../../../../styles/modernListingPageSx";

interface SwagstoreWalletTableProps {
  rows: WalletApiRow[];
  loading: boolean;
  pageNo: number;
  limit: number;
  totalCount: number;
  sortType: SortType;
  onSortChange: (sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onViewTransactions: (row: WalletApiRow) => void;
}

const CREATED_DATE_COLUMN = "created_date";

const SwagstoreWalletTable: React.FC<SwagstoreWalletTableProps> = ({
  rows,
  loading,
  pageNo,
  limit,
  totalCount,
  sortType,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onViewTransactions,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  return (
    <Paper sx={modernTablePaperSx(theme)}>
      {loading ? (
        <Box
          sx={{
            minHeight: "40vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ ...modernEmptyStateSx(theme), minHeight: "20vh" }}>
          <Typography variant="body1">No wallets found</Typography>
        </Box>
      ) : (
        <Box>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    minWidth="100px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Wallet ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="220px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Customer
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="140px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    User
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Balance
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    sortColumn={CREATED_DATE_COLUMN}
                    sortBy={CREATED_DATE_COLUMN}
                    sortDirection={sortType}
                    onSortChange={(_column, newSortType) =>
                      onSortChange(newSortType)
                    }
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Created Date
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="160px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Actions
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`wallet-${row.id}`} hover sx={modernTableRowSx(theme)}>
                    <TableCell sx={modernTableBodyCellSx(theme)}>{row.id.toString()}</TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      <Box sx={{ lineHeight: 1.2 }}>
                        <CustomTypography
                          variant="body3"
                          fontWeight={600}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {row.customer_name || "—"}
                        </CustomTypography>
                        <CustomTypography
                          variant="body3"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          ID: {row.customer_id}
                        </CustomTypography>
                      </Box>
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {row.user_name || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...modernTableBodyCellSx(theme),
                        fontWeight: 700,
                        color: colors.accent,
                      }}
                    >
                      {formatWalletBalance(row.balance)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {formatDate(row.created_date, true)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      <AppButton
                        variant="grayscale"
                        label="View Transactions"
                        onClick={() => onViewTransactions(row)}
                        customStyles={{
                          minHeight: "24px",
                          fontSize: "0.68rem",
                          px: 1.25,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={pageNo}
            limit={limit}
            totalCount={totalCount}
            onPageChange={onPageChange}
            handleRowsChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(SwagstoreWalletTable);
